import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface ScrollState {
  plpScrollPositions: Record<string, { position: number; timestamp: number }>;
  plpPageIndices: Record<string, number>;
  setPlpScrollPosition: (path: string, position: number) => void;
  setPlpPageIndex: (path: string, index: number) => void;
  getPlpScrollPosition: (path: string) => number;
  getPlpPageIndex: (path: string) => number;
  clearPlpScrollPosition: (path: string) => void;
  isScrollRestored: boolean;
  setScrollRestored: (value: boolean) => void;
  // New methods for better state management
  resetScrollState: () => void;
  hasScrollData: (path: string) => boolean;
}

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

// Migration function to handle state updates
const migrate = (persistedState: any, version: number) => {
  if (version === 0) {
    // Initial migration - ensure all required fields exist
    return {
      ...persistedState,
      plpScrollPositions: persistedState.plpScrollPositions || {},
      plpPageIndices: persistedState.plpPageIndices || {},
      isScrollRestored: false,
    };
  }
  return persistedState;
};

export const useScrollStore = create<ScrollState>()(
  persist(
    (set, get) => ({
      plpScrollPositions: {},
      plpPageIndices: {},
      isScrollRestored: false,
      setScrollRestored: (value: boolean) => set({ isScrollRestored: value }),

      setPlpScrollPosition: (path: string, position: number) => {
        set(state => ({
          plpScrollPositions: {
            ...state.plpScrollPositions,
            [path]: { position, timestamp: Date.now() },
          },
        }));
      },

      setPlpPageIndex: (path: string, index: number) => {
        set(state => ({
          plpPageIndices: { ...state.plpPageIndices, [path]: index },
        }));
      },

      getPlpScrollPosition: (path: string) => {
        const scrollData = get().plpScrollPositions[path];
        if (!scrollData) return 0;

        // Check if data is expired
        if (Date.now() - scrollData.timestamp > CACHE_DURATION) {
          get().clearPlpScrollPosition(path);
          return 0;
        }

        return scrollData.position;
      },

      getPlpPageIndex: (path: string) => {
        const index = get().plpPageIndices[path];
        if (!index) return 1;

        // Check if corresponding scroll position exists and is valid
        const scrollData = get().plpScrollPositions[path];
        if (!scrollData || Date.now() - scrollData.timestamp > CACHE_DURATION) {
          return 1;
        }

        return index;
      },

      clearPlpScrollPosition: (path: string) => {
        set(state => {
          const { [path]: _, ...restPositions } = state.plpScrollPositions;
          const { [path]: __, ...restIndices } = state.plpPageIndices;
          return {
            plpScrollPositions: restPositions,
            plpPageIndices: restIndices,
          };
        });
      },

      resetScrollState: () => {
        set({ isScrollRestored: false });
      },

      hasScrollData: (path: string) => {
        const scrollData = get().plpScrollPositions[path];
        return !!(
          scrollData && Date.now() - scrollData.timestamp <= CACHE_DURATION
        );
      },
    }),
    {
      name: 'scroll-storage',
      version: 1,
      migrate,
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        plpScrollPositions: state.plpScrollPositions,
        plpPageIndices: state.plpPageIndices,
      }),
    }
  )
);
